import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../stores/authStore';

const fmtDatum = (s: any) => {
  if (!s) return '—';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(s);
  return m[3] + '.' + m[2] + '.' + m[1];
};

const ajpesUrl = (d: any) => {
  const base = 'https://www.ajpes.si/eObjave/rezultati.asp?Sodisce=-1&MAXREC=50&id_skupina=51&podrobno=0';
  if (d.davcna) return base + '&DS=' + encodeURIComponent(d.davcna);
  if (d.maticna) return base + '&MS=' + encodeURIComponent(d.maticna);
  return 'https://www.ajpes.si/eObjave/';
};

const klas = (v: any) => {
  const t = String(v || '').toLowerCase();
  if (t.includes('likvidacij'))
    return { label: 'Likvidacija', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' };
  if (t.includes('poravnav'))
    return { label: 'Prisilna poravnava', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' };
  if (t.includes('zapu'))
    return { label: 'Stečaj zapuščine', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300' };
  if (t.includes('osebn'))
    return { label: 'Osebni stečaj', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' };
  if (t.includes('prestrukt'))
    return { label: 'Prestrukturiranje', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' };
  if (t.includes('stečaj') || t.includes('stecaj'))
    return { label: 'Stečaj', cls: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' };
  return { label: v || '—', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
};

export function InsolventnostPage() {
  const { selectedCompany } = useAuthStore();  // PATCH_INSOLVENTNOST_FE_V1
  const companyId = selectedCompany?.id || '';
  const [postavke, setPostavke] = useState<any[]>([]);
  const [vrste, setVrste] = useState<string[]>([]);
  const [obseg, setObseg] = useState<'moji' | 'vsi'>('moji');
  const [vrsta, setVrsta] = useState('');
  const [iskanje, setIskanje] = useState('');
  const [nalagam, setNalagam] = useState(false);
  const [stran, setStran] = useState(1);
  const NA_STRAN = 50;

  const nalozi = () => {
    setNalagam(true);
    api.get('/insolventnost', { params: { obseg, ...(companyId ? { company_id: companyId } : {}) } })
      .then((r: any) => {
        setPostavke(Array.isArray(r.data?.postavke) ? r.data.postavke : []);
        setVrste(Array.isArray(r.data?.vrste) ? r.data.vrste : []);
      })
      .catch(() => toast.error('Napaka pri nalaganju insolventnosti'))
      .finally(() => setNalagam(false));
  };
  useEffect(nalozi, [obseg, companyId]);
  useEffect(() => { setStran(1); }, [obseg, vrsta, iskanje]);

  const q = iskanje.trim().toLowerCase();
  const prikazani = postavke.filter((d: any) => {
    if (vrsta && String(d.vrsta_postopka || '') !== vrsta) return false;
    if (q && ![d.naziv, d.maticna, d.davcna, d.opravilna_st]
      .some((x: any) => String(x ?? '').toLowerCase().includes(q))) return false;
    return true;
  });

  const stStrani = Math.max(1, Math.ceil(prikazani.length / NA_STRAN));
  const stranVarna = Math.min(stran, stStrani);
  const odRez = (stranVarna - 1) * NA_STRAN;
  const stranPostavke = prikazani.slice(odRez, odRez + NA_STRAN);

  const stMojih = postavke.filter((d: any) => d.je_moj).length;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-500" /> Insolventnost
        </h1>
        <span className="text-sm text-gray-400">
          {nalagam ? 'Nalagam …' : `Prikazanih: ${prikazani.length}`}
          {obseg === 'vsi' && !nalagam ? ` · mojih: ${stMojih}` : ''}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          {([['moji', 'Moj šifrant'], ['vsi', 'Vsi']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setObseg(id)}
              className={clsx('px-3 py-1.5 text-sm font-medium transition-all',
                obseg === id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700')}>
              {label}
            </button>
          ))}
        </div>

        <select value={vrsta} onChange={(e) => setVrsta(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm">
          <option value="">Vse vrste postopkov</option>
          {vrste.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <input value={iskanje} onChange={(e) => setIskanje(e.target.value)}
          placeholder="Iskanje po nazivu, matični, davčni …"
          className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />

        <button onClick={nalozi} title="Osveži"
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-3 py-3 text-left">Naziv</th>
              <th className="px-3 py-3 text-left whitespace-nowrap">Matična</th>
              <th className="px-3 py-3 text-left whitespace-nowrap">Davčna</th>
              <th className="px-3 py-3 text-left">Postopek</th>
              <th className="px-3 py-3 text-left">Zadnje dejanje</th>
              <th className="px-3 py-3 text-left whitespace-nowrap">Objava</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {prikazani.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                {nalagam ? 'Nalagam …'
                  : (obseg === 'moji'
                    ? 'Noben partner iz vašega šifranta ni v insolventnih postopkih.'
                    : 'Ni zadetkov.')}
              </td></tr>
            )}
            {stranPostavke.map((d: any) => {
              const k = klas(d.vrsta_postopka);
              return (
                <tr key={d.id}
                  className={clsx('border-b border-gray-100 dark:border-gray-700',
                    obseg === 'vsi' && d.je_moj && 'bg-emerald-50/60 dark:bg-emerald-900/10')}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{d.naziv || '—'}</span>
                      {d.novo && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white">novo</span>
                      )}
                      {obseg === 'vsi' && d.je_moj && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">moj šifrant</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono text-xs whitespace-nowrap">{d.maticna || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono text-xs whitespace-nowrap">{d.davcna || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', k.cls)}>{k.label}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{d.procesno_dejanje || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDatum(d.datum_objave)}</td>
                  <td className="px-3 py-2 text-right">
                    {(d.davcna || d.maticna) && (
                      <a href={ajpesUrl(d)} target="_blank" rel="noreferrer"
                        title="Odpri na AJPES" className="inline-flex text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {stStrani > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button onClick={() => setStran((s) => Math.max(1, s - 1))} disabled={stranVarna <= 1}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700">
            ← Prejšnja
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Stran {stranVarna} od {stStrani}
          </span>
          <button onClick={() => setStran((s) => Math.min(stStrani, s + 1))} disabled={stranVarna >= stStrani}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700">
            Naslednja →
          </button>
        </div>
      )}
    </div>
  );
}
